/** 本地上传参考图的固定业务目录名（与 {@code glyphicai.upload.root} / 七牛链路分离） */
export const LIBRARY_REFERENCE_UPLOAD_SEGMENT = '上传的参考图'

/** 参考图单文件大小上限（字节），与后端 ReferenceUploadService.MAX_FILE_SIZE 及容器 multipart 上限对齐 */
export const REFERENCE_IMAGE_MAX_BYTES = 8 * 1024 * 1024

/** 参考图单文件大小上限的可读文案 */
export const REFERENCE_IMAGE_MAX_SIZE_LABEL = '8MB'

/** 旧导出名，与 {@link LIBRARY_REFERENCE_UPLOAD_SEGMENT} 相同 */
export const LIBRARY_DISK_UPLOAD_SEGMENT = LIBRARY_REFERENCE_UPLOAD_SEGMENT

/** 规范 UI 文件夹路径片段（Trim、正反斜杠、削首尾 /） */
export function normalizeLibraryUiFolderPath(raw: string): string {
  return raw
    .trim()
    .replaceAll('\\', '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+|\/+$/g, '')
}

/** 是否落在本地参考图业务子树「上传的参考图」 */
export function isUnderReferenceUploadSubtreeUiPath(uiPathRaw: string): boolean {
  const u = normalizeLibraryUiFolderPath(uiPathRaw)
  return u === LIBRARY_REFERENCE_UPLOAD_SEGMENT || u.startsWith(`${LIBRARY_REFERENCE_UPLOAD_SEGMENT}/`)
}

/** 顶层「上传的参考图」系统目录本体不可删除；其下用户子文件夹仍可删 */
export function isSystemReferenceUploadRootFolder(uiPathRaw: string): boolean {
  return normalizeLibraryUiFolderPath(uiPathRaw) === LIBRARY_REFERENCE_UPLOAD_SEGMENT
}

/** 并入主图库 Trie 的路径：排除参考图业务子树，避免与侧栏并列节点重复 */
export function filteredPathsForGalleryMainTrieMerge(paths: readonly string[]): string[] {
  return (paths ?? []).filter((p) => !isUnderReferenceUploadSubtreeUiPath(p))
}

/** 文件夹整夹选取时部分浏览器不给 MIME，用扩展名兜底 */
export function isLikelyImageUploadFile(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true
  return /\.(jpe?g|png|gif|webp)$/i.test(file.name)
}

/** 上传到图库时使用的业务路径：选在「图库根」时使用 {@link LIBRARY_REFERENCE_UPLOAD_SEGMENT} */
export function libraryDiskUploadBasePath(selectedFolderPath: string): string {
  const t = normalizeLibraryUiFolderPath(selectedFolderPath)
  return t === '' ? LIBRARY_REFERENCE_UPLOAD_SEGMENT : t
}

/**
 * 生成图「保存到图库」：`folderPath` 与侧栏一致；**根目录为空串**→ 后端按图库（七牛命名空间）入库，
 * 不可复用 {@link libraryDiskUploadBasePath}（否则会误落入「上传的参考图」仅磁盘子树）。
 */
export function libraryGallerySaveFolderPath(selectedFolderPathUi: string): string {
  return normalizeLibraryUiFolderPath(selectedFolderPathUi)
}

/** 创作页等：参考图落库 folderPath（保留本地子路径） */
export function referenceImageUploadFolderPath(file: File): string {
  return libraryFolderPathForDiskUpload(LIBRARY_REFERENCE_UPLOAD_SEGMENT, file)
}

/**
 * 使用「选择文件夹」（webkitRelativePath）时，将本地子目录并入业务 folderPath（不含文件名）。
 * 不包含 `..` 段。
 */
export function libraryFolderPathForDiskUpload(selectedFolderUiPath: string, file: File): string {
  const base = libraryDiskUploadBasePath(selectedFolderUiPath)
  const rel =
    typeof (file as File & { webkitRelativePath?: string }).webkitRelativePath === 'string'
      ? (file as File & { webkitRelativePath?: string }).webkitRelativePath!.trim()
      : ''
  if (!rel) return base
  const parts = rel.replaceAll('\\', '/').split('/').filter(Boolean)
  parts.pop()
  const sub = parts.join('/').replace(/\/+/g, '/').replace(/^\/+|\/+$/g, '')
  if (!sub || sub.includes('..')) return base
  return `${base}/${sub}`
}

/** 图库虚拟根在 UI 中的展示名（与面包屑「图库」一致） */
export const LIBRARY_VIRTUAL_ROOT_LABEL = '图库'

/**
 * 业务 folderPath 转为可读路径（图库 / 段1 / 段2 …）。
 * @param uiFolderPath 规范化后的业务路径，根下为空串
 */
export function formatLibraryFolderDisplayPath(uiFolderPath: string): string {
  const p = normalizeLibraryUiFolderPath(uiFolderPath)
  if (!p) return LIBRARY_VIRTUAL_ROOT_LABEL
  return [LIBRARY_VIRTUAL_ROOT_LABEL, ...p.split('/').filter(Boolean)].join(' / ')
}

/**
 * 新建单层文件夹时的完整路径预览（随名称草稿更新）；名称为空时用「…」占位末段。
 */
export function formatNewFolderPreviewDisplayPath(parentUiPath: string, newSegmentDraft: string): string {
  const base = formatLibraryFolderDisplayPath(parentUiPath)
  const seg = newSegmentDraft.trim()
  if (!seg) return `${base} / …`
  const resolved = resolveChildFolderUnderParent(parentUiPath, seg)
  if (!resolved) return `${base} / ${seg}`
  return formatLibraryFolderDisplayPath(resolved)
}

/** 在已选上级目录下新建单层文件夹名（禁止斜杠与 ..） */
export function resolveChildFolderUnderParent(parentUiPath: string, segment: string): string {
  const seg = segment
    .trim()
    .replaceAll('\\', '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+|\/+$/g, '')
  if (!seg || seg.includes('/') || seg.includes('..')) return ''
  const parent = normalizeLibraryUiFolderPath(parentUiPath)
  if (!parent) return seg
  return `${parent}/${seg}`
}
